import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  TreeSelect,
  message,
  Spin
} from 'antd';

import api from '../services/api';

const { TextArea } = Input;

const MaterialGroupModal = ({
  visible,
  editingGroup,
  allGroups,
  tenantId,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);

  // 转换分组数据为TreeSelect格式
  const convertToTreeSelectData = (groups) => {
    if (!groups || groups.length === 0) return [];
    
    return groups.map(group => ({
      title: group.name,
      value: group.id,
      key: group.id,
      children: group.children ? convertToTreeSelectData(group.children) : []
    }));
  };

  // 构建树形结构
  const buildTree = (allGroups) => {
    if (!allGroups || allGroups.length === 0) return [];
    
    // 过滤掉虚拟节点（防护性措施）
    const realGroups = allGroups.filter(group => 
      group && group.id !== null && group.id !== -1 && typeof group.id === 'number'
    );
    
    if (realGroups.length === 0) return [];
    
    // 创建分组映射
    const groupMap = new Map();
    realGroups.forEach(group => {
      groupMap.set(group.id, { ...group, children: [] });
    });
    
    // 构建父子关系
    const rootGroups = [];
    realGroups.forEach(group => {
      const groupWithChildren = groupMap.get(group.id);
      if (group.parentId && groupMap.has(group.parentId)) {
        groupMap.get(group.parentId).children.push(groupWithChildren);
      } else {
        rootGroups.push(groupWithChildren);
      }
    });
    
    return rootGroups;
  };

  // 初始化树形数据
  useEffect(() => {
    if (allGroups && allGroups.length > 0) {
      const tree = buildTree(allGroups);
      const convertedTree = convertToTreeSelectData(tree);
      setTreeData(convertedTree);
    } else {
      setTreeData([]);
    }
  }, [allGroups]);

  // 设置表单初始值
  useEffect(() => {
    if (visible) {
      if (editingGroup) {
        if (editingGroup.id) {
          // 编辑模式 - 编辑现有分组
          form.setFieldsValue({
            name: editingGroup.name,
            description: editingGroup.description,
            parentId: editingGroup.parentId
          });
        } else {
          // 创建子分组模式 - editingGroup只包含parentId
          form.setFieldsValue({
            name: '',
            description: '',
            parentId: editingGroup.parentId
          });
        }
      } else {
        // 新建根分组模式
        form.resetFields();
      }
    }
  }, [visible, editingGroup, form]);

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const data = {
        ...values,
        tenantId
      };

      let response;
      if (editingGroup && editingGroup.id) {
        // 更新分组
        response = await api.put(`/material-groups/${editingGroup.id}`, data);
      } else {
        // 创建分组（包括创建子分组）
        response = await api.post('/material-groups', data);
      }

      if (response.data.success) {
        message.success(editingGroup && editingGroup.id ? '更新成功' : '创建成功');
        form.resetFields();
        onSuccess();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('分组操作失败:', error);
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error('操作失败: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // 验证分组名称是否重复
  const validateGroupName = async (_, value) => {
    if (!value) return Promise.resolve();

    try {
      const parentId = form.getFieldValue('parentId');
      const response = await api.get('/material-groups/check-name', {
        params: {
          tenantId,
          name: value,
          parentId: parentId || null,
          excludeId: editingGroup && editingGroup.id ? editingGroup.id : null
        }
      });

      if (response.data.success && response.data.data.isDuplicate) {
        return Promise.reject(new Error('同一父分组下已存在相同名称的分组'));
      }
    } catch (error) {
      console.error('验证分组名称失败:', error);
    }

    return Promise.resolve();
  };

  // 过滤父分组选项（编辑时不能选择自己和子分组）
  const filterTreeNode = (node) => {
    if (!editingGroup) return true;
    
    // 不能选择自己
    if (node.value === editingGroup.id) return false;
    
    // 不能选择自己的子分组 - 这里需要根据实际的分组路径判断
    // 简化处理：暂时允许所有选项，后端会做验证
    return true;
  };

  return (
    <Modal
      title={editingGroup && editingGroup.id ? '编辑分组' : '新建分组'}
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      width={500}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            name="name"
            label="分组名称"
            rules={[
              { required: true, message: '请输入分组名称' },
              { max: 100, message: '分组名称不能超过100个字符' },
              { validator: validateGroupName }
            ]}
          >
            <Input
              placeholder="请输入分组名称"
            />
          </Form.Item>

          <Form.Item
            name="parentId"
            label="父分组"
            tooltip="选择父分组，留空则创建为根分组"
          >
            <TreeSelect
              placeholder="请选择父分组（可留空）"
              allowClear
              showSearch
              treeDefaultExpandAll
              treeData={treeData}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              filterTreeNode={(inputValue, treeNode) => {
                // 搜索过滤 - 添加安全检查
                if (!treeNode || !treeNode.title) return false;
                return treeNode.title.toLowerCase().includes(inputValue.toLowerCase());
              }}
              treeNodeFilterProp="title"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="分组描述"
            rules={[
              { max: 500, message: '描述不能超过500个字符' }
            ]}
          >
            <TextArea
              placeholder="请输入分组描述（可选）"
              rows={3}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default MaterialGroupModal; 